import { ActualityHqWithCongestionsRow } from '@warebee/shared/dataset-queries';
import {
  ActivityFeedDataColumn,
  ItemSetDataTableColumn,
} from '@warebee/shared/export-converter';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { formatDateTime } from '../../common/formatHelper';
import useFormatter from '../../common/useFormatter';
import ButtonLocateLocation from '../../components/ButtonLocateLocation';
import { ColumnConfig } from '../../components/DatasetTable';
import useActivityFeedDataTableConfig from '../../datasetObject/useActivityFeedDataTableConfig';
import { TagId } from '../../helpContext/TagId';
import { getActivityFeedSchema } from '../../import/activityFeed/schema/activityFeedSchema';
import useLayoutDataTableConfig from '../../layout/useLayoutDataTableConfig';
import ActualityEntityTag from '../components/ActualityIEntityTag';
import { feedDatasetExtraFields } from '../store/feed.state';

function useActualityCongestionDataTableConfig(): ColumnConfig<ActualityHqWithCongestionsRow>[] {
  const { t: tImporter } = useTranslation('importer');
  const { t } = useTranslation('dataset');
  const activityExtraFields = useRecoilValue(feedDatasetExtraFields);

  const formatter = useFormatter();

  const schema = _.keyBy(getActivityFeedSchema(tImporter).fields, f => f.name);

  const feedFieldSkip: Set<ActivityFeedDataColumn> = new Set(['duration']);

  const itemsFieldSkip: Set<ItemSetDataTableColumn> = new Set([
    'consignee',
    'sku',
  ]);
  const locationFieldSkip: Set<string> = new Set(['__typename', 'locationId']);

  const feedFields = useActivityFeedDataTableConfig().filter(
    f => !feedFieldSkip.has(f.field),
  ) as any as ColumnConfig<ActualityHqWithCongestionsRow>[];
  // const itemSetFields = useItemSetDataTableConfig().filter(
  //   f => !itemsFieldSkip.has(f.field),
  // ) as any as ColumnConfig<ActualityHqWithCongestionsRow>[];

  const locationFields = useLayoutDataTableConfig().filter(
    f => !locationFieldSkip.has(f.field),
  ) as any as ColumnConfig<ActualityHqWithCongestionsRow>[];

  const feedExtraFields: ColumnConfig<ActualityHqWithCongestionsRow>[] = _.map(
    activityExtraFields,
    f => ({
      field: f.alias as any,
      title: f.title ?? f.name,
      hasFilter: true,
      hasSearch: true,
    }),
  );

  const columnsConfig: ColumnConfig<ActualityHqWithCongestionsRow>[] = [
    ..._.filter(feedFields, f =>
      _.includes(
        [
          'eventId',
          'eventProcessType',
          'sku',
          'locationId',
          'eventEndTime',
          'jobId',
          'agentId',
        ],
        f.field,
      ),
    ),
    {
      field: 'congestedEvents',
      title: t`Congested Events IDs`,
      hasSort: true,
      hasFilter: true,
      render: (events: string[]) =>
        _.map(events, v => <TagId showFull value={v} />),
    },
    {
      field: 'congestedLocations',
      title: t`Congested Locations`,
      hasSort: true,
      hasFilter: true,
      render: (events: string[]) =>
        _.map(events, locationId => (
          <ButtonLocateLocation locationId={locationId} />
        )),
    },

    {
      field: 'congestedJobs',
      title: t`Congested Jobs`,
      hasSort: true,
      hasFilter: true,
      render: (events: string[], row) =>
        _.map(events, jobId => (
          <ActualityEntityTag
            entity="job"
            title={jobId}
            filters={{ agentId: row['agentId'], jobId }}
          />
        )),
    },
    {
      field: 'congestedAgentIds',
      title: t`Congested Agent`,
      hasSort: true,
      hasFilter: true,
      render: (events: string[], row) =>
        _.map(events, agentId => (
          <ActualityEntityTag
            entity="agentId"
            title={agentId}
            filters={{ agentId }}
          />
        )),
    },
    {
      field: 'congestedEventTime',
      title: t`Events time`,
      render: (events: string[], row) =>
        _.map(events, v => formatDateTime(new Date(v))),
    },
  ];

  return columnsConfig;
}
export default useActualityCongestionDataTableConfig;
